/**
 * Calcule le pourcentage de progression d'une liste de courses.
 * @param {Array} items - Articles de la liste
 * @returns {number} Pourcentage entre 0 et 100
 */
export const calculateProgress = (items) => {
  if (!items || items.length === 0) return 0;
  const completedCount = items.filter(item => item.checked).length;
  return Math.round((completedCount / items.length) * 100);
};

/**
 * Associe un nom d'ingrédient à une catégorie de courses.
 * @param {string} ingredientName
 * @returns {string} Nom de la catégorie
 */
export const mapIngredientToCategory = (ingredientName) => {
  const name = ingredientName.toLowerCase();
  if (/lait|beurre|fromage|crème|yaourt|œuf|oeuf|oeufs/i.test(name)) return 'Produits frais';
  if (/pomme|orange|banane|fraise|poire|fruit/i.test(name)) return 'Fruits';
  if (/tomate|salade|carotte|poireau|oignon|légume/i.test(name)) return 'Légumes';
  if (/poulet|bœuf|porc|viande|steak|côte/i.test(name)) return 'Viandes';
  if (/poisson|saumon|thon|cabillaud|crevette/i.test(name)) return 'Poissons';
  if (/eau|jus|vin|bière|soda|boisson/i.test(name)) return 'Boissons';
  if (/farine|sucre|sel|huile|pâtes|riz|conserve/i.test(name)) return 'Épicerie';
  return 'Épicerie';
};

/**
 * Fusionne une liste d'ingrédients en dédupliquant par nom.
 * Additionne les quantités numériques si même unité.
 * @param {Array} ingredients
 * @returns {Array}
 */
export const mergeIngredients = (ingredients) => {
  const merged = {};

  ingredients.forEach(ingredient => {
    const key = ingredient.name.toLowerCase();

    if (!merged[key]) {
      merged[key] = { ...ingredient };
    } else {
      if (merged[key].unit === ingredient.unit) {
        const existingNum = Number(merged[key].quantity);
        const newNum = Number(ingredient.quantity);
        if (!isNaN(existingNum) && !isNaN(newNum)) {
          merged[key].quantity = existingNum + newNum;
        }
      }
      merged[key].recipes = [...new Set([...merged[key].recipes, ...ingredient.recipes])];
      merged[key].recipeIds = [...new Set([...merged[key].recipeIds, ...ingredient.recipeIds])];
    }
  });

  return Object.values(merged);
};

/**
 * Fusionne deux listes d'articles de courses.
 * Additionne les quantités si même nom et même unité.
 * @param {Array} existingItems
 * @param {Array} newItems
 * @returns {Array}
 */
export const mergeShoppingItems = (existingItems, newItems) => {
  const merged = [...existingItems];
  const existingMap = {};

  existingItems.forEach((item, index) => {
    existingMap[item.name.toLowerCase()] = index;
  });

  newItems.forEach(newItem => {
    const key = newItem.name.toLowerCase();

    if (existingMap[key] !== undefined) {
      const index = existingMap[key];
      const existingItem = merged[index];

      if (existingItem.unit === newItem.unit) {
        const existingNum = Number(existingItem.quantity);
        const newNum = Number(newItem.quantity);
        if (!isNaN(existingNum) && !isNaN(newNum)) {
          merged[index] = { ...merged[index], quantity: existingNum + newNum };
        }
      }

      merged[index] = {
        ...merged[index],
        recipes: [...new Set([...existingItem.recipes, ...newItem.recipes])],
        recipeIds: [...new Set([...(existingItem.recipeIds || []), ...(newItem.recipeIds || [])])],
      };
    } else {
      merged.push(newItem);
    }
  });

  return merged;
};
